import { RefObject, useEffect, useState } from 'react';

export interface UseInViewportOptions {
  /** Forwarded to `IntersectionObserver` — how far outside the viewport counts as "near". */
  rootMargin?: string;
}

const DEFAULT_ROOT_MARGIN = '400px 0px';

/**
 * Whether the observed element has EVER intersected the viewport (within `rootMargin`).
 *
 * Freezes at `true` the first time it fires rather than tracking live visibility: a caller using
 * this to lazy-fetch wants "has this been seen once", not "is it on screen right now" — the latter
 * would flip already-loaded content back to a loading state the moment the user scrolls it away.
 *
 * Fails open to `true` when `IntersectionObserver` is unavailable (old browser, or a test that has
 * not mocked it) — a caller lazy-gating a fetch on this should fetch immediately rather than never,
 * so an environment that cannot observe is treated as "already visible".
 */
export const useInViewport = (
  ref: RefObject<Element>,
  { rootMargin = DEFAULT_ROOT_MARGIN }: UseInViewportOptions = {}
): boolean => {
  const [hasBeenVisible, setHasBeenVisible] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (hasBeenVisible || typeof IntersectionObserver === 'undefined' || !ref.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setHasBeenVisible(true);
        }
      },
      { rootMargin }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [ref, hasBeenVisible, rootMargin]);

  return hasBeenVisible;
};

export default useInViewport;
